import { useCallback, useState } from 'react'

import { GetServerSideProps, NextPage } from 'next'
import Head from 'next/head'
import { useRouter } from 'next/router'

import { withIronSessionSsr } from 'iron-session/next'

import { chatNewChatSpace } from '~/api-client/food_api/chat'
import { userGetMe } from '~/api-client/food_api/user'
import {
  ChatSpaceMember,
  ChatSpaceUser,
  User,
} from '~/api-definition/definition'
import { ChatSpacePermissionType } from '~/api-definition/enumTypes'
import { Button } from '~/components/action/Button'
import { Modal } from '~/components/action/Modal'
import { MemberSelectList } from '~/components/food_app/member/MemberSelectModal'
import { Footer } from '~/components/layout/Footer'
import { useSession } from '~/provider/SessionProvider'
import { sessionOptions } from '~/session'

type Props = {
  loginUser?: User
}

const Page: NextPage<Props> = (props) => {
  const router = useRouter()
  const session = useSession({ loginRequired: true })
  const accessToken = session.response?.data?.accessToken

  const [changeMode, setChangeMode] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [createError, setCreateError] = useState(false)
  const [participants, setParticipants] = useState<
    Pick<ChatSpaceMember, 'user' | 'permissionType'>[]
  >(
    props.loginUser
      ? [
          {
            user: props.loginUser,
            permissionType: 'manager',
          },
        ]
      : []
  )

  const handleMemberAdded = (
    user: ChatSpaceUser,
    permissionType: ChatSpacePermissionType
  ) => {
    setParticipants((participants) => {
      return [...participants, { user, permissionType }]
    })
  }

  const handleMemberChanged = (
    user: ChatSpaceUser,
    permissionType: ChatSpacePermissionType
  ) => {
    setParticipants((participants) => {
      return participants.map((p) =>
        p.user.userId === user.userId ? { user, permissionType } : p
      )
    })
  }

  const handleMemberDeleted = (user: ChatSpaceUser) => {
    setParticipants((participants) => {
      return participants.filter((p) => p.user.userId !== user.userId)
    })
  }

  const handleCreate = useCallback(async () => {
    try {
      setIsCreating(true)

      if (accessToken != null && participants.length >= 2) {
        const chatSpace = await chatNewChatSpace({
          accessToken,
          body: {
            space_type: 'group',
            group_name: '',
            group_members: participants.map((p) => ({
              user_id: p.user.userId,
              permission_type: p.permissionType,
            })),
            passed_messages_visible: true,
          },
        })
        await router.push(`/chat_spaces/${chatSpace.space.chatSpaceId}`)
      }
    } catch (e) {
      console.error(e)
      setCreateError(true)
    } finally {
      setIsCreating(false)
    }
  }, [accessToken, participants, router])

  return (
    <div className="min-h-full">
      <Head>
        <title>グループ参加者選択</title>
      </Head>

      <Modal
        isOpen={createError}
        title={'エラーが発生しました'}
        actionButtons={[
          <div key={0} className="flex justify-center">
            <Button onClick={() => router.push(`/chat_spaces`)}>戻る</Button>
          </div>,
        ]}
      />

      <MemberSelectList
        target="ChatSpaceMember"
        title="グループ参加者選択"
        primaryButtonText="このメンバーでグループを作成"
        memberTypes={[
          { value: 'member', label: '参加者' },
          { value: 'manager', label: '管理者' },
        ]}
        members={[]}
        membersAdded={participants}
        onMemberChanged={handleMemberChanged}
        onMemberAdded={handleMemberAdded}
        onMemberDeleted={handleMemberDeleted}
        onSubmit={handleCreate}
        onCancel={() => router.back()}
      />

      <Footer />
    </div>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = withIronSessionSsr(
  async (context) => {
    const loginUser = context.req.session.user
      ? await userGetMe({
          accessToken: context.req.session.user.accessToken,
        })
      : undefined
    const props = {
      loginUser: loginUser?.user,
    }
    return { props }
  },
  sessionOptions
)

export default Page
